/**
 * Schedules — Missions programmées (cron tasks)
 */

import { useState, useEffect } from "react";
import { api } from "../lib/api";

interface ScheduledTask {
  id: string;
  name: string;
  icon: string;
  description: string;
  cron: string;
  isTemplate: boolean;
  active: boolean;
  retry: number;
  notifChannels: string[];
  owner: string;
  createdAt: string;
}

interface ScheduledRun {
  id: string;
  taskId: string;
  startedAt: string;
  finishedAt: string | null;
  result: string;
  retryUsed: number;
  errorMessage?: string;
}

export default function Schedules() {
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [runs, setRuns] = useState<Record<string, ScheduledRun[]>>({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      setTasks((await api.scheduledTasks.list()) as unknown as ScheduledTask[]);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (id: string, active: boolean) => {
    await api.scheduledTasks.update(id, { active: !active });
    load();
  };

  const runNow = async (_id: string) => {
    await api.scheduledTasks.create({ name: "Manual run", cron: "* * * * *" });
  };

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">automatisation</div>
          <h1>Missions programmées</h1>
          <p>{tasks.length} tâche(s) · {tasks.filter(t => t.active).length} active(s)</p>
        </div>
      </div>

      {loading && <div className="no-results">Chargement…</div>}

      {!loading && tasks.length === 0 && (
        <div className="no-results">Aucune tâche programmée. Crée-en une !</div>
      )}

      {!loading && tasks.map((task) => (
        <div key={task.id} className="ops-card" style={{ opacity: task.active ? 1 : 0.5 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <span style={{ fontSize: "24px" }}>{task.icon || "⚙"}</span>
            <strong style={{ fontSize: "15px" }}>{task.name}</strong>
            {task.isTemplate && <span className="cmd-badge" style={{ color: "var(--blue)" }}>template</span>}
            <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
              <button className="btn" style={{ fontSize: "11px" }} onClick={() => runNow(task.id)}>▶ Exécuter</button>
              <button className="btn" style={{ fontSize: "11px" }} onClick={() => toggleActive(task.id, task.active)}>
                {task.active ? "⏸ Désactiver" : "▶ Activer"}
              </button>
            </div>
          </div>
          <div style={{ fontSize: "13px", color: "var(--muted)" }}>{task.description}</div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "11px", marginTop: "8px", paddingTop: "8px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
            <span style={{ color: "var(--muted)" }}>⏰ <strong style={{ color: "var(--text)", fontFamily: "var(--mono)" }}>{task.cron}</strong></span>
            <span style={{ color: "var(--muted)" }}>🔄 Retry: <strong style={{ color: "var(--text)" }}>{task.retry}</strong></span>
            <span style={{ color: "var(--muted)" }}>📢 {Array.isArray(task.notifChannels) ? task.notifChannels.join(", ") : task.notifChannels}</span>
            <span style={{ color: "var(--muted)" }}>👑 <strong style={{ color: "var(--text)" }}>{task.owner}</strong></span>
          </div>
        </div>
      ))}
    </div>
  );
}
